// The wallet position's cost-basis fold, on the client — a line-for-line
// mirror of backend/pkg/positions/fold.go (acquire / dispose) and verify.go
// (value), so a live trade moves realized and unrealized exactly as the
// server will once it folds the same trade.
//
//   method      average cost, known units first: a sale draws on the units
//               whose cost is known (at the known pool's average cost), then
//               on unknown-cost units (their proceeds are never profit)
//   buy         priced: q_known += qty, basis_known += usd
//               unpriced: q_unknown += qty
//   sell a      oversell beyond q_known + q_unknown joins q_unknown;
//               k = min(a, q_known), u = a − k
//               cb = basis_known × k / q_known (all of it when k = q_known)
//               priced: realized_known += usd × k / a − cb
//               q_known −= k, basis_known −= cb (0 when q_known reaches 0),
//               q_unknown −= u
//   unrealized  q_known / 10^decimals × mark − basis_known
//
// Not mirrored (the server corrects them on its next answer): the bonding
// curve's token-fee gross-up of a curve buy's basis, and the raw-inexact
// slack that closes a double-rounded walk.

/** The fold's inputs on a row, as the positions read sends them. */
export type FoldState = {
  qKnownRaw: bigint
  qUnknownRaw: bigint
  basisKnownUsd: number
  realizedKnownUsd: number
  decimals: number
}

const SCALE = 10n ** 18n

/** n / d as a float, exact to ~1e-18 relative (fold.go ratioFloat). */
export function ratio(n: bigint, d: bigint): number {
  if (d === 0n) return 0
  const neg = n < 0n !== d < 0n
  const a = n < 0n ? -n : n
  const b = d < 0n ? -d : d
  const whole = a / b
  const frac = Number(((a % b) * SCALE) / b) / 1e18
  const v = Number(whole) + frac
  return neg ? -v : v
}

/** A buy of `qty` raw units worth `usd` (null = unpriced). */
export function foldBuy(s: FoldState, qty: bigint, usd: number | null): FoldState {
  if (qty <= 0n) return s
  if (usd == null || !Number.isFinite(usd)) return { ...s, qUnknownRaw: s.qUnknownRaw + qty }
  return { ...s, qKnownRaw: s.qKnownRaw + qty, basisKnownUsd: s.basisKnownUsd + usd }
}

/** A sale of `a` raw units for `usd` (null = unpriced). */
export function foldSell(s: FoldState, a: bigint, usd: number | null): FoldState {
  if (a <= 0n) return s
  let qUnknown = s.qUnknownRaw
  const avail = s.qKnownRaw + qUnknown
  if (a > avail) qUnknown += a - avail // an oversell: units arrived another way
  const k = a < s.qKnownRaw ? a : s.qKnownRaw
  const u = a - k
  let cb = 0
  if (k > 0n) cb = k === s.qKnownRaw ? s.basisKnownUsd : s.basisKnownUsd * ratio(k, s.qKnownRaw)
  let realized = s.realizedKnownUsd
  if (usd != null && Number.isFinite(usd) && k > 0n) realized += usd * ratio(k, a) - cb
  const qKnown = s.qKnownRaw - k
  return {
    ...s,
    qKnownRaw: qKnown,
    qUnknownRaw: qUnknown - u,
    basisKnownUsd: qKnown === 0n ? 0 : s.basisKnownUsd - cb,
    realizedKnownUsd: realized,
  }
}

/**
 * The proceeds of a sale with no known cost (fold.go dispose: usd × u / a,
 * u = the units beyond q_known, oversell included) — never profit; the
 * row's proceeds_without_basis_usd. 0 for an unpriced sale.
 */
export function saleProceedsWithoutBasis(s: FoldState, a: bigint, usd: number | null): number {
  if (a <= 0n || usd == null || !Number.isFinite(usd)) return 0
  const k = a < s.qKnownRaw ? a : s.qKnownRaw
  const u = a - k
  return u > 0n ? usd * ratio(u, a) : 0
}

/** Unrealized at `mark` dollars per token (verify.go value). */
export function unrealizedAt(s: FoldState, mark: number): number | null {
  if (!Number.isFinite(mark) || mark <= 0) return null
  if (!Number.isInteger(s.decimals) || s.decimals < 0 || s.decimals > 30) return null
  return ratio(s.qKnownRaw, 10n ** BigInt(s.decimals)) * mark - s.basisKnownUsd
}

/**
 * The fold's inputs as the positions read sends them on a row (contract from
 * the API lane: q_known_raw, q_unknown_raw, basis_known_usd,
 * realized_known_usd, token_decimals). Until a row carries them all, the
 * live layer leaves its PnL to the server — it never invents one.
 */
export type FoldFields = {
  q_known_raw?: string | null
  q_unknown_raw?: string | null
  basis_known_usd?: number | null
  realized_known_usd?: number | null
  token_decimals?: number | null
}

const big = (v: string | null | undefined): bigint | null => {
  if (typeof v !== 'string' || v === '') return null
  try {
    return BigInt(v)
  } catch {
    return null
  }
}

/** A row's fold state; null when the row does not carry every input. */
export function foldOf(row: FoldFields): FoldState | null {
  const qk = big(row.q_known_raw)
  const qu = big(row.q_unknown_raw)
  const basis = row.basis_known_usd
  const realized = row.realized_known_usd
  const dec = row.token_decimals
  if (qk == null || qu == null) return null
  if (typeof basis !== 'number' || !Number.isFinite(basis)) return null
  if (typeof realized !== 'number' || !Number.isFinite(realized)) return null
  if (typeof dec !== 'number' || !Number.isInteger(dec) || dec < 0) return null
  return {
    qKnownRaw: qk,
    qUnknownRaw: qu,
    basisKnownUsd: basis,
    realizedKnownUsd: realized,
    decimals: dec,
  }
}

/** The fold state back onto the row's input fields. */
export function withFold<T extends FoldFields>(row: T, s: FoldState): T {
  return {
    ...row,
    q_known_raw: s.qKnownRaw.toString(),
    q_unknown_raw: s.qUnknownRaw.toString(),
    basis_known_usd: s.basisKnownUsd,
    realized_known_usd: s.realizedKnownUsd,
  }
}

/** human → raw units of a token with `decimals` (display precision). */
export function humanToRaw(human: number, decimals: number): bigint {
  if (!Number.isFinite(human) || human <= 0) return 0n
  const [int, frac = ''] = human.toFixed(Math.min(decimals, 20)).split('.')
  const digits = `${int}${frac.padEnd(decimals, '0').slice(0, decimals)}`
  try {
    return BigInt(digits)
  } catch {
    return 0n
  }
}
